import * as React from 'react';
import styled from 'styled-components';
import LoaderWithContainer from '../molecules/LoaderWithContainer';

const STORAGE_KEY = 'inspectionsInfoBlock.expanded';

const InspectionsInfoBlock = ({ info }: Props) => {
  const [expanded, setExpanded] = React.useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? false : stored === 'true';
  });

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    localStorage.setItem(STORAGE_KEY, String(next));
  };

  if (!info?.length) {
    return (
      <InfoWrapper>
        <LoaderWithContainer />
      </InfoWrapper>
    );
  }
  return (
    <InfoWrapper>
      <ToggleRow>
        <ToggleButton type="button" onClick={toggle} aria-expanded={expanded}>
          <Chevron $open={expanded}>▾</Chevron>
          {expanded ? 'Hide details' : 'Show details'}
        </ToggleButton>
      </ToggleRow>
      {expanded && (
        <InfoList>
          {info?.map((infoLine, index) => (
            <InfoRow key={index}>
              <InfoLabel>{infoLine?.label}:</InfoLabel>
              <InfoValue>{infoLine?.value || '—'}</InfoValue>
            </InfoRow>
          ))}
        </InfoList>
      )}
    </InfoWrapper>
  );
};

interface Props {
  info: Array<Info>;
}

interface Info {
  label: string;
  value: string;
  gridColumn?: string;
}

const InfoWrapper = styled.div`
  text-align: left;
  position: relative;
  margin-bottom: var(--space-4);
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-family: inherit;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Chevron = styled.span<{ $open: boolean }>`
  display: inline-block;
  transition: transform 0.15s ease;
  transform: rotate(${({ $open }) => ($open ? '0deg' : '-90deg')});
  color: var(--neutral-500);
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InfoRow = styled.div`
  font-size: var(--fs-base);
  color: var(--neutral-900);
  line-height: 1.5;
`;

const InfoLabel = styled.span`
  font-weight: var(--fw-bold);
  color: var(--neutral-900);
  margin-right: 8px;
`;

const InfoValue = styled.span`
  font-weight: var(--fw-regular);
  color: var(--neutral-800);
`;

export default InspectionsInfoBlock;
